import clsx from "clsx";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Plus } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { BillingAccountSummary } from "@/components/Portal/Documents/BillingAccounts/BillingAccountSummary";
import { sortBillingAccounts } from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { DrAccount } from "@/services/model";

const optionClassMapping = {
  selected: "border-border-primary-inverse bg-surface-secondary-inverse",
  unselected:
    "border-transparent bg-surface-tertiary-inverse hover:bg-surface-secondary-inverse",
};

interface Props {
  accounts: DrAccount[];
  selectedId?: number;
  hint?: ReactNode;
  onSelect: (accountId: number) => void;
  onCreate: () => void;
  onClose: () => void;
}

/** Picks a Vertragskonto from the customer's list, or starts creating one. */
export const BillingAccountPickerModal: FC<Props> = ({
  accounts,
  selectedId,
  hint,
  onSelect,
  onCreate,
  onClose,
}) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts",
  });

  return (
    <Modal
      className="w-full"
      title={
        <h2 className="text-headline-sm font-semibold leading-md">
          {t("picker.title")}
        </h2>
      }
      variant="dark"
      onClose={onClose}
    >
      <div
        data-testid="billing-account-picker"
        className="flex flex-col gap-10 pt-2"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-base font-semibold leading-md text-foreground-primary-inverse">
              {t("picker.listHeadline")}
            </h3>
            <Button
              dataTestid="billing-account-picker-add"
              variant="secondary"
              size="sm"
              inverted
              icon={<Plus />}
              onClick={onCreate}
            >
              {t("add")}
            </Button>
          </div>
          <ul className="flex flex-col gap-4">
            {sortBillingAccounts(accounts).map((account, index) => {
              const accountId = account.id;
              const isSelected = accountId != null && accountId === selectedId;

              return (
                <li key={accountId ?? index}>
                  <button
                    data-testid="billing-account-picker-option"
                    className={clsx(
                      "flex w-full rounded-md border p-4 text-left transition-colors",
                      optionClassMapping[
                        isSelected ? "selected" : "unselected"
                      ],
                    )}
                    type="button"
                    aria-pressed={isSelected}
                    disabled={accountId == null}
                    onClick={() => accountId != null && onSelect(accountId)}
                  >
                    <BillingAccountSummary
                      account={account}
                      variant="inverted"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        {hint}
      </div>
    </Modal>
  );
};
